import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, ExternalLink, X, Check, Sparkles } from 'lucide-react';
import { useRoleTrainingRecommendations, useDismissRecommendation, useCompleteRecommendation } from '@/hooks/useRoleTrainingRecommendations';

interface Props { userId: string; }

export function ManagerLearningRecommendations({ userId }: Props) {
  const { data: recommendations, isLoading } = useRoleTrainingRecommendations(userId, 'manager');
  const dismissMutation = useDismissRecommendation();
  const completeMutation = useCompleteRecommendation();

  if (isLoading) return <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><BookOpen className="h-5 w-5" />Leadership Development</CardTitle></CardHeader><CardContent><div className="text-center py-4 text-muted-foreground">Loading...</div></CardContent></Card>;

  if (!recommendations?.length) {
    return (
      <Card><CardHeader><CardTitle className="text-lg flex items-center gap-2"><BookOpen className="h-5 w-5" />Leadership Development</CardTitle><CardDescription>Personalized leadership training recommendations</CardDescription></CardHeader>
        <CardContent><div className="text-center py-6"><Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground" /><p className="text-sm text-muted-foreground">No recommendations at this time.</p></div></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-lg flex items-center gap-2"><BookOpen className="h-5 w-5" />Leadership Development</CardTitle><CardDescription>Personalized recommendations (advisory only)</CardDescription></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {recommendations.map((rec) => (
            <div key={rec.id} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
              <div className="flex-1">
                <Badge variant="outline" className="text-xs mb-1">{rec.categoryKey}</Badge>
                {rec.artifact ? <h4 className="font-medium text-sm">{rec.artifact.title}</h4> : <p className="text-sm">{rec.reason}</p>}
              </div>
              <div className="flex gap-1">
                {rec.artifact?.accessUrl && <Button variant="ghost" size="icon" className="h-8 w-8" asChild><a href={rec.artifact.accessUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>}
                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" onClick={() => completeMutation.mutate(rec.id)} disabled={completeMutation.isPending}><Check className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dismissMutation.mutate(rec.id)} disabled={dismissMutation.isPending}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
